import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { Role } from 'library/types';
import apiService from 'library/api-service';
import { renderDataGridLocaleText } from 'utils';
import { useSingleRowSelectionGuard } from 'hooks';
import { useEffect, useMemo, useState } from 'react';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CreateRoleDialog, UpdateRoleDialog } from './components';
import { GridColDef, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-premium';

const Roles = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // useState hooks
  const [data, setData] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDeleteRole, setOpenDeleteRole] = useState(false);
  const [openCreateRole, setOpenCreateRole] = useState(false);
  const [openUpdateRole, setOpenUpdateRole] = useState(false);
  const [cloneRoleId, setCloneRoleId] = useState<GridRowId | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: intl.formatMessage({ id: 'projects.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const getValues = async () => {
    try {
      setLoading(true);

      const response = await apiService.getRoles();

      if (response.isSuccess) {
        setData(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const handleCloseDeleteRole = () => setOpenDeleteRole(false);
  const handleClickOpenDeleteRole = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;
    setOpenDeleteRole(true);
  };

  const handleCloseCreateRole = () => {
    setOpenCreateRole(false);
    setTimeout(() => {
      setCloneRoleId(null);
    }, 200);
  };
  const handleClickOpenCreateRole = () => setOpenCreateRole(true);

  const handleClickOpenCloneRole = () => {
    const selectedRoleId = requireSingleRowSelection(rowSelectionModel);
    if (selectedRoleId === null) return;

    setCloneRoleId(selectedRoleId);
    setOpenCreateRole(true);
  };

  const handleCloseUpdateRole = () => setOpenUpdateRole(false);
  const handleClickOpenUpdateRole = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;
    setOpenUpdateRole(true);
  };

  // useEffect hooks
  useEffect(() => {
    getValues();
  }, []);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-role']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateRole}>
              <FormattedMessage id="roles.createRole" defaultMessage="Rol Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['add-role']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <Button
              onClick={handleClickOpenCloneRole}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<ContentCopyIcon />}
              color="info">
              <FormattedMessage id="roles.cloneRole" defaultMessage="Rol Kopyala" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-role']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateRole}>
              <FormattedMessage id="roles.updateRole" defaultMessage="Rol Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-role']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteRole}>
              <FormattedMessage id="roles.deleteRole" defaultMessage="Rol Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateRoleDialog
        open={openCreateRole}
        cloneRoleId={cloneRoleId}
        handleClose={handleCloseCreateRole}
        refresh={getValues}
      />
      <UpdateRoleDialog
        open={openUpdateRole}
        roleId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateRole}
        refresh={getValues}
      />
      <DeleteDialog
        open={openDeleteRole}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteRole}
        refresh={getValues}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="roles.deleteRole" defaultMessage="Rol Sil" />}
        subTitle={
          <FormattedMessage
            id="roles.deleteRoleRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra rol veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteRole}
        successMessage={
          <FormattedMessage
            id="roles.deleteRoleCompleted"
            defaultMessage="Seçili rolü silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={data}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default Roles;
